import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Trophy, Crown, Medal, Award, Music, Calendar, Sparkles, PenTool, Users } from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  artist: Music,
  album: Trophy,
  festival: Calendar,
  young: Sparkles,
  composer: PenTool,
  bagad: Users,
};

const Resultats = () => {
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  usePageVisibility('page_resultats_enabled');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, resultsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/votes/results`)
      ]);
      setCategories(catRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index) => {
    switch(index) {
      case 0: return <Crown className="w-6 h-6 text-gold" />;
      case 1: return <Medal className="w-5 h-5 text-gray-300" />;
      case 2: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <Award className="w-4 h-4 text-white/30" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  const hasResults = Object.keys(results).some(k => results[k]?.length > 0);

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="resultats-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <h1 className="font-syne text-4xl md:text-6xl font-extrabold tracking-tighter uppercase text-white mb-6">
          Palmarès<br />
          <span className="text-gold-gradient">2026</span>
        </h1>
        <p className="text-lg text-white/60 max-w-2xl">
          Découvrez les lauréats des Stered an Noz Awards 2026, élus par le public.
        </p>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        {!hasResults ? (
          <div className="text-center py-20 border border-white/10 bg-obsidian-light">
            <Trophy className="w-16 h-16 text-white/20 mx-auto mb-6" />
            <h2 className="font-syne text-2xl font-bold text-white mb-4">Résultats à venir</h2>
            <p className="text-white/50 max-w-md mx-auto">
              Les résultats seront dévoilés à la fin de la phase de vote. Restez connecté !
            </p>
          </div>
        ) : (
          <div className="space-y-16">
            {categories.map(category => {
              const Icon = categoryIcons[category.id] || Trophy;
              const categoryResults = results[category.id] || [];

              if (categoryResults.length === 0) return null;

              return (
                <div key={category.id} data-testid={`results-category-${category.id}`}>
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 flex items-center justify-center border border-gold/30 bg-gold/5">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h2 className="font-syne text-2xl font-bold text-white">{category.name}</h2>
                      <p className="text-sm text-white/50 italic">{category.name_breton}</p>
                    </div>
                  </div>

                  {/* Results List */}
                  <div className="space-y-4">
                    {categoryResults.slice(0, 4).map((nominee, index) => (
                      <div
                        key={nominee.id}
                        className={`flex items-center gap-6 p-6 border transition-all ${
                          index === 0
                            ? 'border-gold bg-gold/5'
                            : 'border-white/10 bg-obsidian-light'
                        }`}
                        data-testid={`result-${nominee.id}`}
                      >
                        {/* Rank */}
                        <div className={`w-16 h-16 flex items-center justify-center flex-shrink-0 ${
                          index === 0 ? 'bg-gold' : 'bg-white/5'
                        }`}>
                          {index === 0 ? (
                            <span className="font-syne text-2xl font-bold text-obsidian">1</span>
                          ) : (
                            <span className="font-syne text-xl font-bold text-white/50">{index + 1}</span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <h3 className={`font-syne font-bold truncate ${
                              index === 0 ? 'text-2xl text-gold' : 'text-xl text-white'
                            }`}>
                              {nominee.nominee_name}
                            </h3>
                            {index === 0 && (
                              <span className="px-3 py-1 bg-gold text-obsidian text-xs uppercase tracking-wider font-bold">
                                Lauréat
                              </span>
                            )}
                          </div>
                          {nominee.description && (
                            <p className="text-sm text-white/50 mt-1 line-clamp-1">{nominee.description}</p>
                          )}
                        </div>

                        {/* Votes */}
                        <div className="text-right flex-shrink-0">
                          <div className={`font-syne text-3xl font-bold ${index === 0 ? 'text-gold' : 'text-white'}`}>
                            {nominee.vote_count || 0}
                          </div>
                          <div className="text-xs text-white/40 uppercase tracking-wider">votes</div>
                        </div>

                        {/* Rank Icon */}
                        <div className="flex-shrink-0">
                          {getRankIcon(index)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default Resultats;
